import { ReceiptLongOutlined, UTurnLeftOutlined } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import {
  ORDER_STATUS,
  PRODUCT_CATEGORY,
  USER_ROLES,
} from "../../../constants/applicationConstants";
import {
  setSingleAlertObj,
  SingleAlertInfo,
} from "../../../redux/slice/commonSlice";
import {
  fetchOrderById,
  getOrdersByPatientId,
  OrderInfo,
  useOrderSlice,
} from "../../../redux/slice/order/OrderSlice";
import {
  getPatientListByPracticeId,
  resetPatientListByPracticeId,
  usePatientReducer,
} from "../../../redux/slice/patient/patientSlice";
import { PracticeInfo } from "../../../redux/slice/practiceSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { useUserReducer } from "../../../redux/slice/userSlice";
import { fullName, sortNames } from "../../../utils/Utility";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { useModalStyles } from "../../common/useModalStyles";
import { useAuthSlice } from "../../../redux/slice/authSlice";

interface ReturnProductModalProps {
  open: boolean;
  onClose: () => void;
}

interface ReturnFormData {
  practice: string | number;
  patientId: string | number;
  orderId: string | number;
}

type DropdownOption = { value: string | number; label: string };

const CreateOrApplyInvoiceModal: React.FC<ReturnProductModalProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    isDark,
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    dropdownWrapSx,
    destructiveBtnSx,
    cancelBtnSx,
    primaryBtnSx
  } = useModalStyles();

  const { practiceByUserRoleUserSlice } = useUserReducer();
  const { patientList } = usePatientReducer();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [eligibleOrders, setEligibleOrders] = useState<OrderInfo[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const { OrderObject } = useOrderSlice();
  const { userInfo } = useAuthSlice();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ReturnFormData>({
    defaultValues: { practice: "", patientId: "", orderId: "" },
    mode: "onSubmit",
  });

  const selectedPractice = watch("practice");
  const selectedPatientId = watch("patientId");
  const selectedOrderId = watch("orderId");

  useEffect(() => {
    if (open) {
      dispatch(resetPatientListByPracticeId());
      setEligibleOrders([]);
      if (practiceByUserRoleUserSlice?.length === 1) {
        const singleId = practiceByUserRoleUserSlice[0].practiceId;
        setValue("practice", singleId);
        loadPatientsForPractice(singleId.toString());
      }
    }
  }, [open]);

  useEffect(() => {
    if (selectedPractice) {
      dispatch(resetPatientListByPracticeId());
      setValue("patientId", "");
      setValue("orderId", "");
      setEligibleOrders([]);
      loadPatientsForPractice(selectedPractice.toString());
    }
  }, [selectedPractice]);

  useEffect(() => {
    if (selectedPatientId) {
      setValue("orderId", "");
      setEligibleOrders([]);
      loadOrdersForPatient(Number(selectedPatientId));
    }
  }, [selectedPatientId]);

  useEffect(() => {
    if (selectedOrderId) {
      dispatch(fetchOrderById({
        orderId: Number(selectedOrderId),
        isSaveAuditLog: true
      }));
    }
  }, [selectedOrderId])

  const invoiceButtonLabel = useMemo(() => {
    if (!selectedOrderId) return "Create or Apply INV";
    // CS-1765 - Use top-level purchaseAndBill from order response instead of manufacturer customConfig
    return OrderObject?.purchaseAndBill === "Y"
      ? "Create INV"
      : "Apply INV";
  }, [selectedOrderId, OrderObject]);

  const loadPatientsForPractice = async (practiceId: string) => {
    setLoadingPatients(true);
    await dispatch(getPatientListByPracticeId({ practiceId }));
    setLoadingPatients(false);
  };

  const loadOrdersForPatient = async (patientId: number) => {
    setLoadingOrders(true);
    try {
      const response = await dispatch(getOrdersByPatientId(patientId));

      if (
        response.payload?.code === "V3OK" &&
        response.payload?.message === "SUCCESS" &&
        (!response.payload?.errors || response.payload.errors.length === 0)
      ) {
        const allOrders: OrderInfo[] = response.payload.data ?? [];

        const isAdminOrSuperAdmin =
          userInfo?.userRole === USER_ROLES.ROLE_ADMIN ||
          userInfo?.userRole === USER_ROLES.ROLE_SUPERADMIN;

        const eligible = allOrders.filter((o) => {
          if (o.productCategory === PRODUCT_CATEGORY.CONSERVATIVE) return false;
          if (o.invoiceStatus === "INVOICED") return false;
          if (o.manufacturerName === "Migrate Manufacturer") return false;
          if (!isAdminOrSuperAdmin && o.orderStatus !== ORDER_STATUS.DELIVERED) return false;
          return true;
        });

        if (eligible.length === 0) {
          showAlert(
            `No eligible orders found for this patient.`,
            "error",
          );
          return;
        }

        setEligibleOrders(eligible);
      } else {
        showAlert(
          "An error occurred while fetching orders for this patient.",
          "error",
        );
      }
    } catch {
      showAlert(
        "An error occurred while fetching orders. Please try again.",
        "error",
      );
    } finally {
      setLoadingOrders(false);
    }
  };

  const showAlert = (
    message: string,
    alertType: "success" | "error" | "info",
  ) => {
    const alertObj: SingleAlertInfo = { message, alertType };
    dispatch(setSingleAlertObj(alertObj));
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setEligibleOrders([]);
      dispatch(resetPatientListByPracticeId());
      reset();
    }, 300);
  };

  const onFormSubmit = async (data: ReturnFormData) => {
    if (!data.patientId || !data.orderId) return;
    setIsSubmitting(true);

    try {
      const selectedOrder = eligibleOrders.find(
        (o) => o.orderId === Number(data.orderId),
      );

      if (!selectedOrder) {
        showAlert("Selected order not found. Please try again.", "error");
        return;
      }

      const matchedPatient = patientList?.find(
        (p: any) => p.patientId === Number(data.patientId),
      );
      const patientName = matchedPatient
        ? fullName(
          "",
          matchedPatient.firstName,
          matchedPatient.middleName,
          matchedPatient.lastName,
        )
        : "";

      const enrichedOrder: OrderInfo = {
        ...selectedOrder,
        patientName,
        manufacturerName: selectedOrder.manufacturerName ?? "",
      };

      // CS-1765 - Use top-level purchaseAndBill from order response instead of manufacturer customConfig
      if (OrderObject?.purchaseAndBill === "Y") {
        navigate("/create_invoice", { state: { selectedOrderBilling: enrichedOrder } });
      } else {
        navigate("/billing", { state: { selectedOrderBilling: enrichedOrder } });
      }

      handleClose();
    } catch {
      showAlert("An error occurred. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const practiceOptions: DropdownOption[] = useMemo(() => {
    if (!practiceByUserRoleUserSlice?.length) return [];
    const sorted = sortNames(practiceByUserRoleUserSlice, "practiceName");
    return sorted.map((p: PracticeInfo) => ({
      value: p.practiceId,
      label: p.practiceName,
    }));
  }, [practiceByUserRoleUserSlice]);

  const patientOptions: DropdownOption[] = useMemo(() => {
    if (!patientList?.length) return [];
    return patientList.map((p: any) => ({
      value: p.patientId,
      label:
        fullName("", p.firstName, p.middleName, p.lastName) +
        " — DOB: " +
        p.birthDate,
    }));
  }, [patientList]);

  const orderOptions: DropdownOption[] = useMemo(() => {
    return eligibleOrders.map((o) => ({
      value: o.orderId,
      label: `${o.orderNumber}${o.productName ? " — " + o.productName : ""}`,
    }));
  }, [eligibleOrders]);

  const FieldLabel = ({
    label,
    required = false,
  }: {
    label: string;
    required?: boolean;
  }) => (
    <Typography
      sx={{
        fontSize: "0.8rem",
        fontWeight: 600,
        color: tokens.textSecondary,
        mb: 0.7,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: tokens.accent }}>
          *
        </Box>
      )}
    </Typography>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(tokens.accent)}>
          <ReceiptLongOutlined
            sx={{ fontSize: "1.2rem", color: tokens.accent }}
          />
        </Box>
        <Box>
          <Typography
            fontWeight={700}
            sx={{
              fontSize: "1.1rem",
              color: tokens.textPrimary,
              lineHeight: 1.3,
            }}
          >
            Create or Apply Invoice
          </Typography>
          <Typography
            sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}
          >
            Select practice, patient and order
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={closeIconSx}>
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Box sx={bodySx}>
          <Box>
            <FieldLabel label="Practice" required />
            <Box sx={dropdownWrapSx(!!errors.practice)}>
              <CustomDropdownField
                name="practice"
                control={control}
                options={practiceOptions}
                rules={{ required: "Please select a practice to continue." }}
                size="small"
                placeholderText="Select a practice..."
                sortOptions={false}
                disabled={practiceOptions.length === 1}
              />
            </Box>
          </Box>
          <Box>
            <FieldLabel label="Patient" required />
            <Box sx={dropdownWrapSx(!!errors.patientId)}>
              <CustomDropdownField
                name="patientId"
                control={control}
                options={patientOptions}
                rules={{ required: "Please select a patient to continue." }}
                size="small"
                placeholderText={
                  !selectedPractice
                    ? "Select a practice first..."
                    : loadingPatients
                      ? "Loading patients..."
                      : patientOptions.length === 0
                        ? "No active patients found..."
                        : "Select a patient..."
                }
                sortOptions={false}
                disabled={!selectedPractice || loadingPatients}
              />
            </Box>
          </Box>
          <Box>
            <FieldLabel label="Delivered Order" required />
            <Box sx={dropdownWrapSx(!!errors.orderId)}>
              <CustomDropdownField
                name="orderId"
                control={control}
                options={orderOptions}
                rules={{ required: "Please select an order to continue." }}
                size="small"
                placeholderText={
                  !selectedPatientId
                    ? "Select a patient first..."
                    : loadingOrders
                      ? "Loading orders..."
                      : orderOptions.length === 0
                        ? "No eligible orders found..."
                        : "Select an order..."
                }
                sortOptions={false}
                disabled={
                  !selectedPatientId ||
                  loadingOrders ||
                  orderOptions.length === 0
                }
              />
            </Box>
          </Box>
        </Box>

        <Box sx={footerSx}>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={isSubmitting || loadingOrders}
              startIcon={
                <UTurnLeftOutlined sx={{ fontSize: "1rem !important" }} />
              }
              sx={primaryBtnSx}
            >
              {isSubmitting ? "Processing..." : invoiceButtonLabel}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
              onClick={handleClose}
              sx={cancelBtnSx}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </Dialog>
  );
};

export default CreateOrApplyInvoiceModal;
